export type KpiIconTone =
  | 'teal'
  | 'green'
  | 'violet'
  | 'pink'
  | 'blue'
  | 'purple'
  | 'amber'
  | 'cyan'

export type KpiValueTone = 'growth' | 'brand'

export type KpiDeltaKind = 'percent' | 'points'

export type KpiValueKind = 'compactCurrency' | 'percent' | 'integer'

export type ManagerialKpi = {
  id: string
  label: string
  icon: string
  iconTone: KpiIconTone
  valueTone: KpiValueTone
  valueKind: KpiValueKind
  value: number
  delta: number
  deltaKind: KpiDeltaKind
}

export type KpiDeltaDirection = 'up' | 'down' | 'flat'

export type ManagerialKpiView = {
  id: string
  label: string
  icon: string
  iconTone: KpiIconTone
  valueTone: KpiValueTone
  formattedValue: string
  formattedDelta: string
  deltaDirection: KpiDeltaDirection
  comparisonLabel: string
  isPlaceholder: boolean
  isBusy: boolean
}

export const KPI_COMPARISON_LABEL = 'vs. período anterior'

/** Paleta compartilhada com os gráficos que entram nesta página. */
export const DASHBOARD_CHART_PALETTE = [
  '#5e17eb',
  '#d4006b',
  '#10b981',
  '#3b82f6',
  '#f59e0b',
  '#8b5cf6',
  '#0d9488',
  '#ef4444',
] as const

function formatDecimalBr(value: number, fractionDigits: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

export function formatCompactCurrencyBrl(value: number): string {
  const absoluteValue = Math.abs(value)

  if (absoluteValue >= 1_000_000) {
    return `R$ ${formatDecimalBr(value / 1_000_000, 2)} mi`
  }

  if (absoluteValue >= 1_000) {
    const thousands = value / 1_000
    const fractionDigits = Number.isInteger(thousands) ? 0 : 1
    return `R$ ${formatDecimalBr(thousands, fractionDigits)} mil`
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
    .format(value)
    .replace(/\u00a0|\u202f/g, ' ')
}

export function formatPercentDisplay(value: number): string {
  return `${formatDecimalBr(value, 1)}%`
}

export function formatIntegerBr(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value)
}

export function formatFullCurrencyBrl(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
    .format(value)
    .replace(/\u00a0|\u202f/g, ' ')
}

export function formatIntegerCurrencyBrl(value: number): string {
  return `R$ ${formatIntegerBr(value)}`
}

export function resolveMarginPercent(revenue: number, profit: number): number {
  if (revenue === 0) return 0
  return (profit / revenue) * 100
}

export function formatDeltaDisplay(value: number, kind: KpiDeltaKind): string {
  const absoluteValue = Math.abs(value)
  if (kind === 'points') {
    return `${formatDecimalBr(absoluteValue, 1)} p.p.`
  }
  return formatPercentDisplay(absoluteValue)
}

export function resolveDeltaDirection(value: number): KpiDeltaDirection {
  if (value > 0) return 'up'
  if (value < 0) return 'down'
  return 'flat'
}

export function formatKpiValue(kpi: ManagerialKpi): string {
  switch (kpi.valueKind) {
    case 'compactCurrency':
      return formatCompactCurrencyBrl(kpi.value)
    case 'percent':
      return formatPercentDisplay(kpi.value)
    case 'integer':
      return formatIntegerBr(kpi.value)
  }
}

export function toManagerialKpiView(kpi: ManagerialKpi): ManagerialKpiView {
  return {
    id: kpi.id,
    label: kpi.label,
    icon: kpi.icon,
    iconTone: kpi.iconTone,
    valueTone: kpi.valueTone,
    formattedValue: formatKpiValue(kpi),
    formattedDelta: formatDeltaDisplay(kpi.delta, kpi.deltaKind),
    deltaDirection: resolveDeltaDirection(kpi.delta),
    comparisonLabel: KPI_COMPARISON_LABEL,
    isPlaceholder: true,
    isBusy: false,
  }
}

export const MANAGERIAL_KPI_DEFINITIONS: Omit<
  ManagerialKpi,
  'value' | 'delta' | 'deltaKind'
>[] = [
  {
    id: 'net-revenue',
    label: 'Volume transacionado (GMV)',
    icon: 'mdi:cash',
    iconTone: 'teal',
    valueTone: 'growth',
    valueKind: 'compactCurrency',
  },
  {
    id: 'operating-profit',
    label: 'Receita da plataforma',
    icon: 'mdi:circle-multiple',
    iconTone: 'green',
    valueTone: 'growth',
    valueKind: 'compactCurrency',
  },
  {
    id: 'operating-margin',
    label: 'Take rate',
    icon: 'mdi:percent',
    iconTone: 'violet',
    valueTone: 'growth',
    valueKind: 'percent',
  },
  {
    id: 'active-clients',
    label: 'Compradores ativos',
    icon: 'mdi:account-outline',
    iconTone: 'pink',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'active-campaigns',
    label: 'Campanhas ativas',
    icon: 'mdi:bullhorn-outline',
    iconTone: 'blue',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'leads-12m',
    label: 'Leads de checkout',
    icon: 'mdi:account-group-outline',
    iconTone: 'purple',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'average-ticket',
    label: 'Ticket médio',
    icon: 'mdi:ticket-confirmation-outline',
    iconTone: 'amber',
    valueTone: 'growth',
    valueKind: 'compactCurrency',
  },
  {
    id: 'active-institutions',
    label: 'Instituições ativas',
    icon: 'mdi:domain',
    iconTone: 'cyan',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'paid-orders',
    label: 'Pedidos pagos',
    icon: 'mdi:receipt-text-check-outline',
    iconTone: 'green',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'sold-numbers',
    label: 'Números vendidos',
    icon: 'mdi:numeric',
    iconTone: 'violet',
    valueTone: 'brand',
    valueKind: 'integer',
  },
  {
    id: 'conversion-rate',
    label: 'Taxa de conversão',
    icon: 'mdi:chart-line',
    iconTone: 'amber',
    valueTone: 'growth',
    valueKind: 'percent',
  },
  {
    id: 'active-affiliates',
    label: 'Afiliados ativos',
    icon: 'mdi:handshake-outline',
    iconTone: 'pink',
    valueTone: 'brand',
    valueKind: 'integer',
  },
]

export function getPlaceholderManagerialKpiViews(): ManagerialKpiView[] {
  return MANAGERIAL_KPI_DEFINITIONS.map(({ id, label, icon, iconTone, valueTone }) => ({
    id,
    label,
    icon,
    iconTone,
    valueTone,
    formattedValue: '—',
    formattedDelta: '',
    deltaDirection: 'flat',
    comparisonLabel: '',
    isPlaceholder: true,
    isBusy: false,
  }))
}
export const ACTIVE_INSTITUTIONS_KPI_ID = 'active-institutions'

export const PAID_ORDERS_KPI_ID = 'paid-orders'

export const AVERAGE_TICKET_KPI_ID = 'average-ticket'

export const ACTIVE_CLIENTS_KPI_ID = 'active-clients'

export const ACTIVE_CAMPAIGNS_KPI_ID = 'active-campaigns'

export const ACTIVE_AFFILIATES_KPI_ID = 'active-affiliates'

export const NET_REVENUE_KPI_ID = 'net-revenue'

export const PLATFORM_PROFIT_KPI_ID = 'operating-profit'
export const PLATFORM_MARGIN_KPI_ID = 'operating-margin'
export const SOLD_NUMBERS_KPI_ID = 'sold-numbers'
export const LEADS_KPI_ID = 'leads-12m'
export const CONVERSION_RATE_KPI_ID = 'conversion-rate'

export const LIVE_MANAGERIAL_KPI_IDS = [
  LEADS_KPI_ID,
  CONVERSION_RATE_KPI_ID,
  PLATFORM_PROFIT_KPI_ID,
  PLATFORM_MARGIN_KPI_ID,
  SOLD_NUMBERS_KPI_ID,
  NET_REVENUE_KPI_ID,
  ACTIVE_INSTITUTIONS_KPI_ID,
  PAID_ORDERS_KPI_ID,
  AVERAGE_TICKET_KPI_ID,
  ACTIVE_CLIENTS_KPI_ID,
  ACTIVE_CAMPAIGNS_KPI_ID,
  ACTIVE_AFFILIATES_KPI_ID,
] as const

export type LiveManagerialKpiId = (typeof LIVE_MANAGERIAL_KPI_IDS)[number]

export const KPI_LOADING_DISPLAY = '—'

export type LiveManagerialKpi = {
  id: string
  label: string
  formatted_value: string
}

export function applyLiveManagerialKpis(
  placeholders: ManagerialKpiView[],
  liveKpis: Partial<Record<LiveManagerialKpiId, LiveManagerialKpi | null>>,
  isLoading: boolean
): ManagerialKpiView[] {
  const liveIds = new Set<string>(LIVE_MANAGERIAL_KPI_IDS)

  return placeholders.map((item) => {
    if (!liveIds.has(item.id)) {
      return item
    }

    const live = liveKpis[item.id as LiveManagerialKpiId]
    if (live) {
      return {
        ...item,
        label: live.label,
        formattedValue: live.formatted_value,
        isPlaceholder: false,
        isBusy: isLoading,
      }
    }

    return {
      ...item,
      formattedValue: KPI_LOADING_DISPLAY,
      isPlaceholder: true,
      isBusy: isLoading,
    }
  })
}
